import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowRightLeft, Calculator } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, PAGE, SHIFT, shiftStatus } from '@kioskgaming/ui';
import { formatInTimezone, formatTimeInTimezone } from '@kioskgaming/ui';
import NoShowActionsPanel from '../components/shifts/NoShowActionsPanel';
import ModalPortal from '../components/ui/ModalPortal';
import { fetchCashierDevices, fetchShopCashiers, fetchShopShift, moveShiftDevice } from '../services/api';
import type { CashierDevice, CashierShift, ShopCashier } from '../types';
import { scheduleShiftPath } from '../utils/cashierSchedule';
import {
  deviceOptionLabel,
  fallbackDeviceLabel,
  MOVE_REASON_MAX,
  MOVE_REASON_MIN,
  shopDeviceErrorMessage,
} from '../utils/cashierDevices';

function statusBadgeClass(status: string): string {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'scheduled') return 'border-blue-200 bg-blue-50 text-blue-800';
  if (status === 'no_show') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (status === 'pending_manager_review' || status === 'pending_reconciliation') {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

const NEVER_STARTED_STATUSES = new Set(['draft', 'no_show', 'cancelled']);

/** Device sentence for the shift: pinned / chosen at start / running on / ran on. */
function shiftDeviceSummary(shift: CashierShift | null, deviceName: string | null): { title: string; detail: string } {
  if (!shift) return { title: '—', detail: '' };
  if (shift.status === 'scheduled') {
    if (shift.deviceId && deviceName) {
      return { title: `Starts on ${deviceName}`, detail: 'The shift can only start on this device.' };
    }
    return { title: 'Chosen at start (any approved device)', detail: 'Bound when the Cashier starts the shift.' };
  }
  if (shift.deviceId && deviceName) {
    if (shift.status === 'active') {
      return { title: `Running on ${deviceName}`, detail: 'Money operations only work on this device.' };
    }
    // A pinned shift that never started (no-show / cancelled) keeps its pin but never ran anywhere.
    if (!shift.actualStartAt && NEVER_STARTED_STATUSES.has(shift.status)) {
      return { title: `Starts on ${deviceName}`, detail: 'The shift never started.' };
    }
    return { title: `Ran on ${deviceName}`, detail: '' };
  }
  return { title: 'No device', detail: 'The shift never started on a device.' };
}

export default function ShiftDetailPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { shiftId } = useParams();
  const id = shiftId ? decodeURIComponent(shiftId) : '';

  const [shift, setShift] = useState<CashierShift | null>(null);
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [devices, setDevices] = useState<CashierDevice[]>([]);
  const [devicesUnavailable, setDevicesUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noShowModalOpen, setNoShowModalOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moving, setMoving] = useState(false);

  const cashierName = useMemo(() => {
    if (!shift) return '—';
    return cashiers.find((c) => c.id === shift.cashierId)?.name || shift.cashierId;
  }, [shift, cashiers]);

  const deviceName = useMemo(() => {
    if (!shift?.deviceId) return null;
    return devices.find((d) => d.id === shift.deviceId)?.deviceName || fallbackDeviceLabel(shift.deviceId);
  }, [shift, devices]);

  const deviceSummary = useMemo(() => shiftDeviceSummary(shift, deviceName), [shift, deviceName]);

  /** Approved devices with no active shift, other than the one the shift is on. */
  const moveTargets = useMemo(
    () => devices.filter((d) => d.status === 'active' && d.activeShift == null && d.id !== shift?.deviceId),
    [devices, shift?.deviceId]
  );

  const moveReasonValid = moveReason.trim().length >= MOVE_REASON_MIN;

  const needsReconciliation = useMemo(() => {
    if (!shift) return false;
    return ['pending_reconciliation', 'pending_manager_review', 'suspended', 'auto_locked'].includes(shift.status);
  }, [shift]);

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const load = async () => {
    if (!id) return;
    setLoading(true);
    // Settled separately: cashier / device lists only decorate the shift and must not blank the page.
    const [shiftResult, cashierResult, deviceResult] = await Promise.allSettled([
      fetchShopShift(id),
      fetchShopCashiers(),
      fetchCashierDevices(),
    ]);
    try {
      if (shiftResult.status === 'rejected') throw shiftResult.reason;
      const shiftRes = shiftResult.value;
      if (!shiftRes.success) throw new Error(shiftRes.message || 'Shift not found');
      setShift(shiftRes.data || null);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load shift');
      setShift(null);
    }
    if (cashierResult.status === 'fulfilled' && cashierResult.value.success) {
      setCashiers(cashierResult.value.data || []);
    }
    if (deviceResult.status === 'fulfilled' && deviceResult.value.success) {
      setDevices(deviceResult.value.data || []);
      setDevicesUnavailable(false);
    } else {
      setDevicesUnavailable(true);
    }
    setLoading(false);
  };

  const openMoveModal = async () => {
    setMoveTargetId('');
    setMoveReason('');
    setMoveOpen(true);
    // Fresh list so "idle" reflects shifts that started since the page loaded.
    try {
      const res = await fetchCashierDevices();
      if (res.success) {
        setDevices(res.data || []);
        setDevicesUnavailable(false);
      }
    } catch {
      /* keep the list we have */
    }
  };

  /** The Move button is disabled until a target and a valid reason are chosen. */
  const onMoveDevice = async (target: CashierShift) => {
    setMoving(true);
    try {
      const res = await moveShiftDevice(target.id, { deviceId: moveTargetId, reason: moveReason.trim() });
      if (!res.success) {
        toast.error(shopDeviceErrorMessage(res, 'Move failed'));
        return;
      }
      toast.success('Shift moved to the new device');
      setMoveOpen(false);
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.shiftDetails}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Monitor a scheduled shift, its actual session, cash movement and audit events.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {shift?.status === 'active' && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => void openMoveModal()}
              >
                <ArrowRightLeft className="h-4 w-4" />
                Move to another device
              </button>
            )}
            {shift?.status === 'no_show' && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                onClick={() => setNoShowModalOpen(true)}
              >
                No-show actions
              </button>
            )}
            {needsReconciliation && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                onClick={() => navigate(`/reconciliation/${encodeURIComponent(id)}`)}
              >
                <Calculator className="h-4 w-4" />
                {SHIFT.reconcileShift}
              </button>
            )}
            {shift?.status === 'auto_locked' && shift.cashierId ? (
              <button
                type="button"
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                onClick={() => navigate(scheduleShiftPath(shift.cashierId))}
              >
                {SHIFT.scheduleShift}
              </button>
            ) : null}
            {!needsReconciliation && (shift?.closedAt || shift?.status.startsWith('completed')) && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => navigate(`/reconciliation/${encodeURIComponent(id)}`)}
              >
                <Calculator className="h-4 w-4" />
                View reconciliation
              </button>
            )}
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => navigate('/shifts')}
            >
              ← Schedule
            </button>
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
        </div>

        {!id ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Invalid shift id.
          </div>
        ) : loading && !shift ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Loading…
          </div>
        ) : !shift ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Shift not found.{' '}
            <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => navigate('/shifts')}>
              Go back
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className={`rounded-xl border p-4 ${statusBadgeClass(shift.status)}`}>
                <div className="text-xs font-medium uppercase tracking-wide opacity-80">Status</div>
                <div className="mt-1 text-lg font-bold">{shiftStatus(shift.status).label}</div>
              </div>
              <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Scheduled</div>
                <div className="mt-1 text-lg font-bold text-stone-900">
                  {formatTimeInTimezone(shift.scheduledStartAt, timezone)}–{formatTimeInTimezone(shift.scheduledEndAt, timezone)}
                </div>
                <div className="mt-0.5 text-xs text-stone-600">{shift.shiftDate || '—'}</div>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-blue-700">Actual start</div>
                <div className="mt-1 text-lg font-bold text-blue-900">{formatTimeInTimezone(shift.actualStartAt || shift.openedAt, timezone)}</div>
                <div className="mt-0.5 text-xs text-blue-700">{formatInTimezone(shift.actualStartAt || shift.openedAt, timezone)}</div>
              </div>
              <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="shift-device-card">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Device</div>
                <div className="mt-1 text-lg font-bold text-stone-900">{deviceSummary.title}</div>
                {deviceSummary.detail ? (
                  <div className="mt-0.5 text-xs text-stone-600">{deviceSummary.detail}</div>
                ) : null}
                {shift.checkedInAt ? (
                  <div className="mt-1 text-xs font-medium text-emerald-700">
                    Checked in {formatInTimezone(shift.checkedInAt, timezone)}
                  </div>
                ) : null}
                {devicesUnavailable ? (
                  <div className="mt-1 text-xs text-amber-700">Device names could not be loaded.</div>
                ) : null}
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-stone-200 bg-white lg:col-span-2">
                <div className="border-b border-stone-200 px-4 py-3">
                  <h2 className="text-base font-semibold">{shift.id.slice(0, 8).toUpperCase()}</h2>
                  <p className="text-sm text-stone-600">{cashierName}</p>
                </div>
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <div className="space-y-2 text-sm">
                    {[
                      ['Cashier', cashierName],
                      ['Device', deviceSummary.title],
                      ['Checked in', formatInTimezone(shift.checkedInAt, timezone)],
                      ['Scheduled start', formatInTimezone(shift.scheduledStartAt, timezone)],
                      ['Scheduled end', formatInTimezone(shift.scheduledEndAt, timezone)],
                      ['Actual start', formatInTimezone(shift.actualStartAt || shift.openedAt, timezone)],
                      ['Actual end', formatInTimezone(shift.actualEndAt || shift.closedAt, timezone)],
                      ['No-show detected', formatInTimezone(shift.noShowDetectedAt, timezone)],
                    ].map(([label, value]) => (
                      <div key={label} className="grid grid-cols-2 gap-2 border-b border-stone-100 pb-2">
                        <span className="text-stone-500">{label}</span>
                        <span className="font-medium text-stone-900">{value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 text-sm">
                    {[
                      [SHIFT.openingCash, shift.openingCashBalance ?? 0],
                      [SHIFT.expectedCash, shift.expectedClosingCash ?? '—'],
                      [SHIFT.countedCash, shift.closingCashCounted ?? '—'],
                      ['Discrepancy', shift.discrepancyAmount ?? '—'],
                      ['Late grace', `${shift.lateGraceMinutes ?? 15} min`],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="flex items-center justify-between gap-2 border-b border-stone-100 pb-2">
                        <span className="text-stone-600">{label}</span>
                        <strong className="text-stone-900">{String(value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
                {shift.note ? (
                  <div className="border-t border-stone-200 px-4 py-3 text-sm text-stone-700">
                    <span className="font-medium text-stone-500">Note: </span>
                    {shift.note}
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-stone-200 bg-white p-4">
                <h3 className="text-sm font-semibold">Lifecycle</h3>
                <p className="text-xs text-stone-500">Scheduled vs actual status</p>
                <div className="mt-4 space-y-3 text-sm">
                  {[
                    { title: 'Scheduled', desc: 'Shift scheduled by the Shop', done: true },
                    {
                      title: 'Device',
                      desc:
                        shift.checkedInAt && shift.status === 'scheduled'
                          ? `Cashier checked in · ${deviceSummary.title}`
                          : deviceSummary.title,
                      // Bound to a device at start (a pin alone on a scheduled / no-show shift is not done).
                      done:
                        Boolean(shift.deviceId) &&
                        shift.status !== 'scheduled' &&
                        (Boolean(shift.actualStartAt) || !NEVER_STARTED_STATUSES.has(shift.status)),
                    },
                    { title: 'Opening cash', desc: shift.openingCashBalance ? `$${shift.openingCashBalance} submitted` : 'Pending', done: !!shift.actualStartAt || !!shift.openedAt },
                    { title: 'Active', desc: shift.status === 'active' ? 'Transactions allowed' : 'Not active', done: shift.status === 'active' },
                    { title: 'Reconciliation', desc: shift.closedAt ? 'Closing count recorded' : 'Waiting for closing count', done: !!shift.closedAt },
                    { title: 'Completed', desc: shift.status.startsWith('completed') ? 'Shift finalized' : 'Pending', done: shift.status.startsWith('completed') },
                  ].map((step) => (
                    <div key={step.title} className="flex gap-3">
                      <div
                        className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                          step.done ? 'bg-emerald-500' : 'bg-stone-300'
                        }`}
                      />
                      <div>
                        <div className="font-semibold text-stone-900">{step.title}</div>
                        <div className="text-xs text-stone-600">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {noShowModalOpen && shift?.status === 'no_show' ? (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
              <NoShowActionsPanel
                shift={shift}
                cashiers={cashiers}
                devices={devices}
                onReassignSuccess={(updated) => {
                  setShift(updated);
                  setNoShowModalOpen(false);
                }}
                onCancelSuccess={(updated) => {
                  setShift(updated);
                  setNoShowModalOpen(false);
                }}
                onReplacementSuccess={(replacement) => {
                  setNoShowModalOpen(false);
                  navigate(`/shifts/${encodeURIComponent(replacement.id)}`);
                }}
                onCancel={() => setNoShowModalOpen(false)}
              />
            </div>
          </div>
        </ModalPortal>
      ) : null}

      {moveOpen && shift ? (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="move-device-title"
              className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            >
              <div id="move-device-title" className="text-lg font-semibold">
                Move to another device
              </div>
              <p className="mt-1 text-sm text-stone-600">
                {deviceName ? `The shift is running on ${deviceName}. ` : ''}
                After the move, deposits, redeems, cash drops and closing only work on the new device.
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <label htmlFor="move-device-target" className="text-sm font-medium text-stone-700">
                    New device *
                  </label>
                  <select
                    id="move-device-target"
                    value={moveTargetId}
                    onChange={(e) => setMoveTargetId(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  >
                    <option value="">Select device</option>
                    {moveTargets.map((d) => (
                      <option key={d.id} value={d.id}>
                        {deviceOptionLabel(d)}
                      </option>
                    ))}
                  </select>
                  {moveTargets.length === 0 ? (
                    <p className="mt-1 text-xs text-amber-700">
                      No idle approved devices. Approve a device under Devices, or wait until its shift ends.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-stone-500">Only approved devices with no running shift are listed.</p>
                  )}
                </div>
                <div>
                  <label htmlFor="move-device-reason" className="text-sm font-medium text-stone-700">
                    Reason *
                  </label>
                  <textarea
                    id="move-device-reason"
                    value={moveReason}
                    onChange={(e) => setMoveReason(e.target.value)}
                    rows={3}
                    maxLength={MOVE_REASON_MAX}
                    placeholder="e.g. Counter 1 screen broke"
                    className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  />
                  <p className={`mt-1 text-xs ${moveReason && !moveReasonValid ? 'text-rose-700' : 'text-stone-500'}`}>
                    At least {MOVE_REASON_MIN} characters. Saved in the audit log.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                  onClick={() => setMoveOpen(false)}
                  disabled={moving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={moving || !moveTargetId || !moveReasonValid}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                  onClick={() => void onMoveDevice(shift)}
                >
                  {moving ? 'Moving…' : 'Move shift'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </AppLayout>
  );
}
